import { nbVal, strValOrUndef } from "@paroi/data-formatters-lib";
import { BACKEND_API_URL, appLog } from "../../context";
import { eventBus } from "../../ui/shared/global-tools/event-bus";
import { forgetSession, getSessionToken } from "../../ui/shared/global-tools/session-storage";
import { showToast } from "../../ui/shared/global-tools/toast-system";
import { STAGING_API_URL } from "../config/env-variables";

export interface PaginationOptions {
  limit: number;
  page: number;
}

export class ClientError extends Error {
  statusCode: number;
  fieldErrors?: Record<string, string[]>;

  constructor(statusCode: number, message: string, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.name = "ClientError";
    this.statusCode = statusCode;
    this.fieldErrors = fieldErrors;
  }
}

export interface FetchQueryResponse<T> {
  data: T;
  pagination?: Pagination;
}

export async function fetchQuery<T>(resource: string): Promise<FetchQueryResponse<T>>;
export async function fetchQuery<T>(
  resource: string,
  options: {
    token: string;
  },
): Promise<FetchQueryResponse<T>>;
export async function fetchQuery<T>(
  resource: string,
  options: {
    body?: string | FormData;
    method: "DELETE";
  },
): Promise<FetchQueryResponse<T> | undefined>;
export async function fetchQuery<T>(
  resource: string,
  options: {
    body: string | FormData;
    method: "POST" | "PUT" | "PATCH";
  },
): Promise<FetchQueryResponse<T> | undefined>;
export async function fetchQuery<T>(
  resource: string,
  options: {
    token?: string;
    body?: string | FormData;
    method?: "POST" | "PUT" | "PATCH" | "DELETE";
  } = {},
): Promise<FetchQueryResponse<T> | undefined> {
  let token = options.token;
  if (!token) token = getSessionToken();
  if (!token) {
    forgetSession();
    window.location.href = "/";
    return;
  }

  try {
    const headers: HeadersInit = {
      Authorization: token ? `Bearer ${token}` : "",
    };
    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const apiUrl = process.env.NODE_ENV === "development" ? STAGING_API_URL : BACKEND_API_URL;
    const response = await fetch(`${apiUrl}/${resource}`, {
      method: options.method ?? "GET",
      headers,
      body: options.body,
    });
    if (response.status < 200 || response.status >= 300) {
      const remoteMessage = await response.text();
      if (response.status >= 400 && response.status < 500) {
        if (response.status === 429) {
          window.location.href = `${window.location.pathname}`;
          return;
        }
        if (!options.token && response.status === 401) {
          forgetSession();
          window.location.href = "/";
          return;
        }
        const messages = getClientErrorMessages(remoteMessage);
        throw new ClientError(
          response.status,
          messages.length === 0 ? "Une erreur est survenue" : messages.join("\n"),
          getClientErrorFieldErrors(remoteMessage),
        );
      }
      throw new Error(`Error '${response.status}' from the server: ${remoteMessage}`);
    }

    const res = await response.json();
    if (res?.success === false) {
      throw new Error(res?.message || "Une erreur est survenue");
    }
    let data = res.data ?? res.form;

    if (!data && res.message) {
      data = {
        message: res.message,
        success: res.success,
        ...(res ?? {}),
      };
    } else if (!data && res.kpis) {
      data = {
        kpis: res.kpis,
        histogram_data: res.histogram_data,
        latest_data: res.latest_data,
      };
    }

    return {
      data,
      pagination: res.meta ? formatPagination(res.meta) : undefined,
    };
  } catch (error) {
    appLog.error("Http client error:", error);
    throw error;
  }
}

function getClientErrorMessages(remoteMessage: string): string[] {
  const messages: string[] = [];
  const res = JSON.parse(remoteMessage);
  const errors = res?.errors;
  if (typeof errors !== "object") {
    messages.push(strValOrUndef(res?.message) ?? "Une erreur est survenue");
  } else {
    for (const [, val] of Object.entries(errors)) {
      if (!Array.isArray(val)) {
        const msg = strValOrUndef(val);
        if (msg) messages.push(msg);
        continue;
      }
      messages.push(val.join("\n"));
    }
  }
  return messages;
}

function getClientErrorFieldErrors(remoteMessage: string): Record<string, string[]> | undefined {
  const res = JSON.parse(remoteMessage);
  const errors = res?.errors;
  if (typeof errors !== "object") return undefined;

  const fieldErrors: Record<string, string[]> = {};
  for (const [attribute, val] of Object.entries(errors)) {
    fieldErrors[attribute] = Array.isArray(val) ? val.map(String) : [String(val)];
  }
  return fieldErrors;
}

export interface ApiRequestResponse<T> {
  data: T;
  pagination?: Pagination;
}

export interface Pagination {
  perPage: number;
  total: number;
}

function formatPagination(data: any): Pagination {
  return {
    perPage: nbVal(data.per_page),
    total: nbVal(data.total),
  };
}

export async function apiRequestWrapper(
  request: () => Promise<void>,
  { handle404 }: { handle404?: boolean } = {},
): Promise<void> {
  try {
    await request();
  } catch (error) {
    appLog.error(error);
    if (handle404 && contains404(error)) {
      eventBus.emit("404");
      return;
    }
    showToast("Une erreur est survenue", { severity: "error" });
    // TODO: Gracefully handle fatal errors
    // eventBus.emit("fatalError", "Unexpected fetch error");
  }
}

export function contains404(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === "string") return error.includes("404");
  if (typeof error === "object") {
    const err = error as any;
    return err?.message.includes("404");
  }
  return false;
}

export interface FetchFileInput {
  url: string;
  headers?: HeadersInit;
}

export async function fetchFile({
  headers,
  url,
}: FetchFileInput): Promise<{ blob: Blob; responseHeaders: Headers }> {
  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  if (response.status < 200 || response.status >= 300) {
    const remoteMessage = await response.text();
    throw new Error(`Error '${response.status}' from the server: ${remoteMessage}`);
  }

  return {
    blob: await response.blob(),
    responseHeaders: response.headers,
  };
}
